import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { isTopmostModalClose, pushModalClose } from './androidBackStack';

// El elemento de cada overlay registrado, para saber cuál contiene a cuál (ver más abajo).
const containers = new WeakMap<() => void, RefObject<Element | null>>();

/**
 * Registra un overlay (diálogo, popover, hoja…) en la pila del Back de Android mientras `active`.
 * Es la pieza base para que el Back —y Escape— cierren primero lo que está más arriba.
 *
 * `onBack` es lo que hace el Back cuando este overlay es el de arriba. Puede cambiar en cada render
 * (lo normal: `onClose={() => setOpen(false)}`); siempre se ejecuta la versión más reciente.
 *
 * Por qué una entrada ESTABLE y no un registro nuevo en cada cambio: `Modal` volvía a registrarse
 * cada vez que cambiaba `onClose`, y cada registro va a lo alto de la pila. Con un diálogo de
 * confirmación abierto sobre otro modal, bastaba que el de abajo se redibujara para quedar encima
 * en la pila: el Back cerraba el modal de abajo y dejaba la confirmación flotando. Ahora el lugar en
 * la pila lo fija el momento en que el overlay se abre, y no se mueve.
 *
 * `container` es el elemento del overlay. Hace falta cuando un overlay puede contener a otro: si los
 * dos aparecen en el mismo render, React ejecuta primero los efectos del de dentro, y por orden de
 * llegada el de fuera quedaría arriba — el Back cerraría al de fuera llevándose al de dentro, aunque
 * ese estuviera ocupado. Con el elemento, el de fuera entra por debajo de lo que contiene.
 *
 * Devuelve una función que dice si este overlay es el de más arriba.
 */
export function useAndroidBackClose(onBack: () => void, active = true, container?: RefObject<Element | null>): () => boolean {
  const latest = useRef(onBack);
  useLayoutEffect(() => {
    latest.current = onBack;
  });

  const entry = useRef<(() => void) | null>(null);
  useEffect(() => {
    if (!active) return undefined;
    const handler = () => latest.current();
    entry.current = handler;
    if (container) containers.set(handler, container);
    const unregister = pushModalClose(handler, (other) => {
      const mine = container?.current;
      const theirs = containers.get(other)?.current;
      return Boolean(mine && theirs && mine !== theirs && mine.contains(theirs));
    });
    return () => {
      unregister();
      if (entry.current === handler) entry.current = null;
    };
  }, [active, container]);

  return useCallback(() => entry.current !== null && isTopmostModalClose(entry.current), []);
}
